import { Component } from "react";
import Header from "../components/header";
import Footer from "../components/footer";
import Hero from "../components/hero";
import OrangeFaqAccordion from "../components/orange_faq_accordion";
import WorkshopCard from "../components/workshop_card";
import WorkshopGrid from "../components/workshop_grid";
import http from "../http-common";

import {
  IoEaselOutline,
  IoEarthOutline,
  IoPeopleOutline,
  IoHardwareChipOutline,
  IoSparklesOutline,
  IoTelescopeOutline,
  IoRibbonOutline,
  IoGitNetworkOutline,
  IoSchoolOutline,
} from "react-icons/io5";

class XlpWorkshops extends Component {
  state = {
    workshops: [],
    loading: true,
    showModal: false,
    modalTitle: "",
    formValues: {
      full_name: "",
      email: "",
      phone: "",
      school: "",
      grade_class: "",
      message: "",
    },
    submitting: false,
    submitSuccess: false,
    submitError: "",
  };

  componentDidMount() {
    window.scrollTo(0, 0);

    http
      .get("/workshops/")
      .then((res) => {
        const data = res.data;
        if (data.success) {
          this.setState({ workshops: data.workshops, loading: false });
        } else {
          console.error("API returned an error:", data.message);
          this.setState({ loading: false });
        }
      })
      .catch((err) => {
        console.error("Failed to fetch workshops:", err);
        this.setState({ loading: false });
      });
  }

  // ── Modal ──────────────────────────────────────────────────────────

  openModal = (title) => {
    this.setState({
      showModal: true,
      modalTitle: title,
      formValues: {
        full_name: "",
        email: "",
        phone: "",
        school: "",
        grade_class: "",
        message: "",
      },
      submitting: false,
      submitSuccess: false,
      submitError: "",
    });
  };

  closeModal = () => {
    this.setState({ showModal: false });
  };

  // ── Form ───────────────────────────────────────────────────────────

  handleFormChange = (e) => {
    this.setState({
      formValues: { ...this.state.formValues, [e.target.name]: e.target.value },
    });
  };

  handleRegisterSubmit = () => {
    const { modalTitle, formValues } = this.state;
    const { full_name, email, phone } = formValues;

    if (!full_name || !email || !phone) {
      this.setState({
        submitError: "Full name, email, and phone are required.",
      });
      return;
    }

    this.setState({ submitting: true, submitError: "" });

    http
      .post("/workshop-register/", {
        workshop_title: modalTitle,
        ...formValues,
      })
      .then((res) => {
        const data = res.data;
        if (data.success) {
          this.setState({ submitting: false, submitSuccess: true });
        } else {
          this.setState({ submitting: false, submitError: data.message });
        }
      })
      .catch(() => {
        this.setState({
          submitting: false,
          submitError: "Something went wrong. Please try again.",
        });
      });
  };

  // ── Render ─────────────────────────────────────────────────────────

  render() {
    const { isLoggedIn } = this.props;
    const {
      workshops,
      loading,
      showModal,
      modalTitle,
      formValues,
      submitting,
      submitSuccess,
      submitError,
    } = this.state;

    const benefitsData = [
      {
        id: 1,
        icon: <IoSparklesOutline size={32} color="#b54a2e" />,
        title: "Hands-On Learning Experience",
        desc: "XLP workshops focus on learning by doing. Students actively participate in activities, and projects that help them understand concepts better than traditional classroom methods.",
      },
      {
        id: 2,
        icon: <IoPeopleOutline size={32} color="#b54a2e" />,
        title: "Develop Future-Ready Skills",
        desc: "Students gain exposure to skills such as critical thinking, creativity, problem solving, and collaboration, which are essential for success in the modern world.",
      },
      {
        id: 3,
        icon: <IoTelescopeOutline size={32} color="#b54a2e" />,
        title: "Exposure to Technologies",
        desc: "Workshops introduce students to future technologies like robotics, artificial intelligence, and digital innovation, helping them understand how technology shapes industries and careers.",
      },
      {
        id: 4,
        icon: <IoRibbonOutline size={32} color="#b54a2e" />,
        title: "Boosts Confidence and Curiosity",
        desc: "Interactive sessions encourage students to ask questions, explore ideas, and building confidence and curiosity towards learning.",
      },
      {
        id: 5,
        icon: <IoGitNetworkOutline size={32} color="#b54a2e" />,
        title: "Real-World Learning Approach",
        desc: "XLP workshops connect concepts with real-world applications, helping students see how knowledge can be used to solve practical problems.",
      },
      {
        id: 6,
        icon: <IoSchoolOutline size={32} color="#b54a2e" />,
        title: "Inspires Innovation and Creativity",
        desc: "Through guided challenges and activities, students are encouraged to think differently, experiment with ideas, and build innovative solutions.",
      },
    ];

    return (
      <>
        <Header isLoggedIn={isLoggedIn} />

        <Hero
          title={
            <>
              Shaping the Innovators
              <br />
              of Tomorrow.
            </>
          }
          description="It takes care of the background so you can stay focused, sharp, and in full control of your day."
          buttonText="Learn More"
          buttonLink="#FAQ"
          imageSrc="/assets-webp/misc/asians_and_hotwheels.webp"
          background="linear-gradient(to bottom, #f0f4f8 0%, #ffffff 100%)"
        />

        {/* STATS BAR */}
        <section
          className="py-4"
          style={{
            background: "linear-gradient(125deg, #b54a2e 0%, #e06c48 100%)",
          }}
        >
          <div className="container">
            <div className="row g-4 text-white text-center align-items-center">
              <div className="col-12 col-md-6 col-lg-3 d-flex align-items-center justify-content-center gap-2">
                <span className="fs-5 fw-medium">20+ Sessions & Workshops</span>
                <IoEaselOutline size={24} className="opacity-75" />
              </div>
              <div className="col-12 col-md-6 col-lg-3 d-flex align-items-center justify-content-center gap-2">
                <span className="fs-5 fw-medium">30+ Global Speakers</span>
                <IoEarthOutline size={24} className="opacity-75" />
              </div>
              <div className="col-12 col-md-6 col-lg-3 d-flex align-items-center justify-content-center gap-2">
                <span className="fs-5 fw-medium">1,000+ Attendees</span>
                <IoPeopleOutline size={24} className="opacity-75" />
              </div>
              <div className="col-12 col-md-6 col-lg-3 d-flex align-items-center justify-content-center gap-2">
                <span className="fs-5 fw-medium">10+ AI Demos</span>
                <IoHardwareChipOutline size={24} className="opacity-75" />
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────────────────────────────────
            FEATURED WORKSHOPS FROM DJANGO
            ───────────────────────────────────────── */}

        {loading ? (
          <div className="text-center py-5">
            <p className="text-secondary">Brewing up Featured Workshops...</p>
          </div>
        ) : workshops.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-secondary">
              No Featured workshops available at the moment.
            </p>
          </div>
        ) : (
          workshops.map((w) => (
            <WorkshopCard
              key={w.id}
              tag={w.tag}
              title={w.title}
              image={w.image}
              imageAlt={w.title}
              description={w.description}
              outcomes={w.outcomes}
              buttonText={w.button_text}
              onRegister={() => this.openModal(w.title)}
            />
          ))
        )}

        {/* ─────────────────────────────────────────
            WORKSHOPS GRID
            ───────────────────────────────────────── */}

        <WorkshopGrid onRegister={this.openModal} />

        {/* ==========================================
            MADE FOR FUTURE CREATORS GRID
            ========================================== */}
        <section className="py-5 bg-white">
          <div
            className="container py-4 py-lg-5"
            style={{ maxWidth: "1100px" }}
          >
            <div className="text-center mb-1 pb-3">
              <h2
                className="display-5 fw-bold text-dark mb-3"
                style={{ letterSpacing: "-1px" }}
              >
                Made for Future Creators.
              </h2>
              <p className="fs-5" style={{ color: "#b54a2e" }}>
                Benefits of XLP Workshops
              </p>
            </div>

            <div className="row g-5">
              {benefitsData.map((item, index) => (
                <div className="col-12 col-md-6 col-lg-4" key={item.id}>
                  <div
                    className={`h-100 ${index < 3 ? "border-bottom pb-4" : ""}`}
                  >
                    <div className="mb-3">{item.icon}</div>
                    <h4 className="h5 fw-semibold text-dark mb-3">
                      {item.title}
                    </h4>
                    <p
                      className="text-secondary mb-0"
                      style={{ fontSize: "15px", lineHeight: "1.6" }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <OrangeFaqAccordion />

        <Footer />

        {/* ==========================================
            REGISTRATION MODAL
            ========================================== */}

        {showModal && (
          <>
            <div
              className="modal show d-block"
              tabIndex="-1"
              role="dialog"
              style={{ zIndex: 1055 }}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content rounded-4 border-0 shadow-lg">
                  {/* HEADER */}
                  <div className="modal-header border-0 pb-0">
                    <h5 className="modal-title fw-bold text-dark">
                      {submitSuccess
                        ? "You're registered!"
                        : "Register for Workshop"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={this.closeModal}
                      aria-label="Close"
                    />
                  </div>

                  {/* BODY */}
                  <div className="modal-body pt-2 pb-3">
                    {submitSuccess ? (
                      // ── Success state ──────────────────────────────
                      <div className="text-center py-3">
                        <p className="fw-semibold text-dark mb-1">
                          Thank you for registering!
                        </p>
                        <p
                          className="text-secondary"
                          style={{ fontSize: "14px" }}
                        >
                          Check your inbox! We've sent you a confirmation email.
                        </p>
                      </div>
                    ) : (
                      // ── Form ───────────────────────────────────────
                      <div className="d-flex flex-column gap-3 mt-2">
                        {/* Workshop — auto filled, read only */}
                        <div>
                          <label
                            className="form-label text-secondary mb-1"
                            style={{ fontSize: "13px" }}
                          >
                            Workshop
                          </label>
                          <input
                            className="form-control"
                            value={modalTitle}
                            readOnly
                            style={{
                              background: "#f8f8f8",
                              color: "#555",
                              cursor: "default",
                            }}
                          />
                        </div>

                        <input
                          className="form-control"
                          name="full_name"
                          placeholder="Full Name *"
                          value={formValues.full_name}
                          onChange={this.handleFormChange}
                        />
                        <input
                          className="form-control"
                          name="email"
                          type="email"
                          placeholder="Email *"
                          value={formValues.email}
                          onChange={this.handleFormChange}
                        />
                        <input
                          className="form-control"
                          name="phone"
                          type="tel"
                          placeholder="Phone Number *"
                          value={formValues.phone}
                          onChange={this.handleFormChange}
                        />
                        <input
                          className="form-control"
                          name="school"
                          placeholder="School / Institution"
                          value={formValues.school}
                          onChange={this.handleFormChange}
                        />
                        <input
                          className="form-control"
                          name="grade_class"
                          placeholder="Grade / Class"
                          value={formValues.grade_class}
                          onChange={this.handleFormChange}
                        />
                        <textarea
                          className="form-control"
                          name="message"
                          placeholder="Message / Note"
                          rows={3}
                          value={formValues.message}
                          onChange={this.handleFormChange}
                        />

                        {/* Error message */}
                        {submitError && (
                          <p
                            className="text-danger mb-0"
                            style={{ fontSize: "14px" }}
                          >
                            {submitError}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* FOOTER */}
                  <div className="modal-footer border-0 pt-0">
                    {submitSuccess ? (
                      <button
                        className="btn btn-brand rounded-2 px-4"
                        onClick={this.closeModal}
                      >
                        Close
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-outline-secondary rounded-2"
                          onClick={this.closeModal}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-brand rounded-2 px-4"
                          onClick={this.handleRegisterSubmit}
                          disabled={submitting}
                        >
                          {submitting ? "Submitting..." : "Register Now"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* BACKDROP */}
            <div
              className="modal-backdrop show"
              style={{ zIndex: 1050 }}
              onClick={this.closeModal}
            />
          </>
        )}
      </>
    );
  }
}

export default XlpWorkshops;
